import { inject, Injectable, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  AiReceptionistFormatters,
  type IPracticeSetupStepDefinition,
  PracticeSetupWizard,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  IPracticeSetupStep,
  IPracticeSetupWizard,
  IStaffer,
  PracticeSetupStepCategory,
  PracticeSetupStepId,
  PracticeSetupStepStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  firstValueFrom,
  getError,
  INamedDocument,
  IReffable,
  isRefChanged$,
  multiFilter,
  Region,
  snapshotDefined,
  Timestamp,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';

export interface IWizardCategorySection {
  category: PracticeSetupStepCategory;
  label: string;
  completed: number;
  total: number;
  steps: WithRef<IPracticeSetupStep>[];
}

function buildCategorySection(
  stepsByCategory: Map<
    PracticeSetupStepCategory,
    WithRef<IPracticeSetupStep>[]
  >,
  category: PracticeSetupStepCategory
): IWizardCategorySection {
  const steps = stepsByCategory.get(category) ?? [];
  return {
    category,
    label: AiReceptionistFormatters.formatCategoryLabel(category),
    completed: steps.filter(
      (step) => step.status === PracticeSetupStepStatus.Complete
    ).length,
    total: steps.length,
    steps,
  };
}

function computeCompletionPercentage(
  steps: WithRef<IPracticeSetupStep>[]
): number {
  if (!steps.length) {
    return 0;
  }
  const completedCount = steps.filter(
    (step) => step.status === PracticeSetupStepStatus.Complete
  ).length;
  return Math.round((completedCount / steps.length) * 100);
}

function buildCategorySections(
  stepsByCategory: Map<PracticeSetupStepCategory, WithRef<IPracticeSetupStep>[]>
): IWizardCategorySection[] {
  return Object.values(PracticeSetupStepCategory)
    .map((category) => buildCategorySection(stepsByCategory, category))
    .filter((section) => section.total > 0);
}

function getRegionFilteredStepsForRegion(
  region: Region | undefined
): IPracticeSetupStepDefinition[] {
  return region ? PracticeSetupWizard.getRegionFilteredSteps(region) : [];
}

interface IPracticeSetupWizardState {
  wizard: WithRef<IPracticeSetupWizard> | undefined;
  steps: WithRef<IPracticeSetupStep>[];
  stepsByCategory: Map<
    PracticeSetupStepCategory,
    WithRef<IPracticeSetupStep>[]
  >;
  region: Region | undefined;
  loading: boolean;
  mutating: boolean;
  error: string | undefined;
}

const initialState: IPracticeSetupWizardState = {
  wizard: undefined,
  steps: [],
  stepsByCategory: new Map(),
  region: undefined,
  loading: false,
  mutating: false,
  error: undefined,
};

@Injectable({
  providedIn: 'root',
})
export class PracticeSetupWizardStore extends ComponentStore<IPracticeSetupWizardState> {
  private _organisationService = inject(OrganisationService);
  private _router = inject(Router);
  readonly wizard = this.selectSignal((state) => state.wizard);
  readonly steps = this.selectSignal((state) => state.steps);
  readonly stepsByCategory = this.selectSignal(
    (state) => state.stepsByCategory
  );
  readonly region = this.selectSignal((state) => state.region);
  readonly loading = this.selectSignal((state) => state.loading);
  readonly mutating = this.selectSignal((state) => state.mutating);
  readonly error = this.selectSignal((state) => state.error);

  readonly regionFilteredSteps = this.selectSignal(
    this.region,
    getRegionFilteredStepsForRegion
  );

  readonly completionPercentage = this.selectSignal(
    this.steps,
    computeCompletionPercentage
  );

  readonly categories = this.selectSignal(
    this.stepsByCategory,
    buildCategorySections
  );

  readonly loadWizard = this.effect(
    (practice$: Observable<IReffable<IPractice>>) =>
      this._buildLoadWizardEffect(practice$)
  );

  readonly loadWizardByRef = this.effect(
    (
      params$: Observable<{
        wizardRef: DocumentReference<IPracticeSetupWizard>;
        region: Region;
      }>
    ) => this._buildLoadWizardByRefEffect(params$)
  );

  readonly completeStep = this.effect(
    (
      params$: Observable<{
        stepId: PracticeSetupStepId;
        completedBy: WithRef<IStaffer>;
        overrideCompleted?: boolean;
      }>
    ) =>
      params$.pipe(
        withLatestFrom(this._organisationService.region$),
        switchMap(([{ stepId, completedBy, overrideCompleted }, region]) =>
          this._runCompleteStep(stepId, completedBy, overrideCompleted, region)
        )
      )
  );

  readonly reopenStep = this.effect(
    (stepId$: Observable<PracticeSetupStepId>) =>
      stepId$.pipe(
        withLatestFrom(this._organisationService.region$),
        switchMap(([stepId, region]) => this._runReopenStep(stepId, region))
      )
  );

  readonly assignStep = this.effect(
    (
      params$: Observable<{
        stepId: PracticeSetupStepId;
        assignee: INamedDocument<IStaffer>;
        dueDate: Timestamp;
        assignedBy: WithRef<IStaffer>;
      }>
    ) =>
      params$.pipe(
        switchMap(({ stepId, assignee, dueDate, assignedBy }) =>
          this._runAssignStep(stepId, assignee, dueDate, assignedBy)
        )
      )
  );

  readonly trackVisit = this.effect(
    (
      params$: Observable<{
        stepId: PracticeSetupStepId;
        route: string;
      }>
    ) =>
      params$.pipe(
        withLatestFrom(this._organisationService.region$),
        switchMap(([{ stepId, route }, region]) =>
          this._runTrackVisit(stepId, route, region)
        )
      )
  );

  readonly dismissWizard = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(switchMap(() => this._runDismissWizard()))
  );

  constructor() {
    super(initialState);
  }

  resetState(): void {
    this.patchState(initialState);
  }

  canComplete(stepId: PracticeSetupStepId): Signal<boolean> {
    return this.selectSignal(this.steps, (steps) => {
      if (!steps) {
        return false;
      }
      const currentStep = steps.find((step) => step.id === stepId);
      return currentStep ? currentStep.validationPassed : false;
    });
  }

  private _buildLoadWizardEffect(
    practice$: Observable<IReffable<IPractice>>
  ): Observable<unknown> {
    return practice$.pipe(
      isRefChanged$(),
      tap(() => this._beginLoading()),
      switchMap((practice) =>
        from(PracticeSetupWizard.getByPractice(practice)).pipe(
          withLatestFrom(this._organisationService.region$),
          switchMap(([wizardDoc, region]) =>
            this._observeWizardForPractice(wizardDoc, region)
          ),
          catchError((error) => this._handleLoadError(error))
        )
      )
    );
  }

  private _buildLoadWizardByRefEffect(
    params$: Observable<{
      wizardRef: DocumentReference<IPracticeSetupWizard>;
      region: Region;
    }>
  ): Observable<unknown> {
    return params$.pipe(
      tap(() => this._beginLoading()),
      switchMap(({ wizardRef, region }) => {
        this.patchState({ region });
        return this._watchWizardAndSteps({ ref: wizardRef }, region);
      }),
      catchError((error) => this._handleLoadError(error))
    );
  }

  private _observeWizardForPractice(
    wizardDoc: WithRef<IPracticeSetupWizard> | undefined,
    region: Region | undefined
  ): Observable<unknown> {
    if (!wizardDoc || !region) {
      this.patchState({ loading: false, wizard: undefined });
      return of(undefined);
    }

    this.patchState({ region });
    this._startBackgroundValidation(wizardDoc, region);
    return this._watchWizardAndSteps(wizardDoc, region);
  }

  private _startBackgroundValidation(
    wizardDoc: WithRef<IPracticeSetupWizard>,
    region: Region
  ): void {
    try {
      void PracticeSetupWizard.resyncSteps(wizardDoc.ref, region);
      void this._validateState(wizardDoc, region);
    } catch {
      // Background validation failures must not block UI loading.
    }
  }

  private _watchWizardAndSteps(
    wizardDoc: IReffable<IPracticeSetupWizard>,
    region: Region
  ): Observable<unknown> {
    return combineLatest([
      Firestore.doc$(wizardDoc.ref),
      Firestore.query$(PracticeSetupWizard.stepCol(wizardDoc)).pipe(
        multiFilter(undeletedFilter)
      ),
    ]).pipe(
      tapResponse(
        ([wizard, steps]) => this._updateWizardState(wizard, steps, region),
        (error) => this._setLoadFailure(error)
      )
    );
  }

  private _beginLoading(): void {
    this.resetState();
    this.patchState({ loading: true, error: undefined });
  }

  private _setLoadFailure(error: unknown): void {
    this.patchState({ loading: false, error: getError(error) });
  }

  private _handleLoadError(error: unknown): Observable<undefined> {
    this._setLoadFailure(error);
    return of(undefined);
  }

  private _beginMutation(): void {
    this.patchState({ mutating: true, error: undefined });
  }

  private _onMutationSuccess(): void {
    this.patchState({ mutating: false });
  }

  private _onMutationError(error: unknown): void {
    this.patchState({ mutating: false, error: getError(error) });
  }

  private _runCompleteStep(
    stepId: PracticeSetupStepId,
    completedBy: WithRef<IStaffer>,
    overrideCompleted: boolean | undefined,
    region: Region | undefined
  ): Observable<unknown> {
    const wizard = this.wizard();
    if (!wizard || !region) {
      return of(undefined);
    }
    this._beginMutation();
    return from(
      PracticeSetupWizard.completeStep(
        wizard.ref,
        stepId,
        completedBy,
        region,
        undefined,
        overrideCompleted
      )
    ).pipe(
      tapResponse(
        () => this._onMutationSuccess(),
        (error) => this._onMutationError(error)
      )
    );
  }

  private _runReopenStep(
    stepId: PracticeSetupStepId,
    region: Region | undefined
  ): Observable<unknown> {
    const wizard = this.wizard();
    if (!wizard || !region) {
      return of(undefined);
    }
    this._beginMutation();
    return from(
      PracticeSetupWizard.reopenStep(wizard.ref, stepId, region)
    ).pipe(
      tapResponse(
        () => this._onMutationSuccess(),
        (error) => this._onMutationError(error)
      )
    );
  }

  private _runAssignStep(
    stepId: PracticeSetupStepId,
    assignee: INamedDocument<IStaffer>,
    dueDate: Timestamp,
    assignedBy: WithRef<IStaffer>
  ): Observable<unknown> {
    const wizard = this.wizard();
    if (!wizard) {
      return of(undefined);
    }
    this._beginMutation();
    return from(
      PracticeSetupWizard.assignStep(
        wizard.ref,
        stepId,
        assignee,
        dueDate,
        assignedBy
      )
    ).pipe(
      tapResponse(
        () => this._onMutationSuccess(),
        (error) => this._onMutationError(error)
      )
    );
  }

  private _runTrackVisit(
    stepId: PracticeSetupStepId,
    route: string,
    region: Region | undefined
  ): Observable<unknown> {
    const wizard = this.wizard();
    if (!wizard?.ref || !region) {
      return of(undefined);
    }
    return from(
      this._trackVisitForCurrentStaffer(wizard.ref, stepId, route, region)
    );
  }

  private async _trackVisitForCurrentStaffer(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    stepId: PracticeSetupStepId,
    route: string,
    region: Region
  ): Promise<void> {
    const staffer = await firstValueFrom(this._organisationService.staffer$);
    if (!staffer) {
      return;
    }
    try {
      await PracticeSetupWizard.trackVisit(
        wizardRef,
        stepId,
        route,
        stafferToNamedDoc(staffer),
        region
      );
    } catch {
      // Page visit tracking is best-effort; failures must not surface to the user.
    }
  }

  private _runDismissWizard(): Observable<unknown> {
    const wizard = this.wizard();
    if (!wizard) {
      return of(undefined);
    }
    this._beginMutation();
    return from(PracticeSetupWizard.dismiss(wizard.ref)).pipe(
      tapResponse(
        async () => {
          this._onMutationSuccess();
          await this._navigateAfterDismiss();
        },
        (error) => this._onMutationError(error)
      )
    );
  }

  private async _navigateAfterDismiss(): Promise<void> {
    const brand = await snapshotDefined(this._organisationService.brand$);
    const practice = await snapshotDefined(this._organisationService.practice$);
    await this._router.navigate([
      brand.slug,
      practice.slug,
      'schedule',
      'timeline',
    ]);
  }

  private _updateWizardState(
    wizard: WithRef<IPracticeSetupWizard>,
    steps: WithRef<IPracticeSetupStep>[],
    region: Region
  ): void {
    const sortedSteps = this._sortAndDeduplicateSteps(steps, region);
    const stepsByCategory = this._groupStepsByCategory(sortedSteps);

    this.patchState({
      wizard,
      steps: sortedSteps,
      stepsByCategory,
      loading: false,
      error: undefined,
    });
  }

  private _sortAndDeduplicateSteps(
    steps: WithRef<IPracticeSetupStep>[],
    region: Region
  ): WithRef<IPracticeSetupStep>[] {
    const filteredSteps = PracticeSetupWizard.getRegionFilteredSteps(region);
    const stepOrderIndex = new Map(
      filteredSteps.map((def, index) => [def.id, index])
    );
    const knownStepIds = new Set(stepOrderIndex.keys());
    const knownSteps = steps.filter((step) => knownStepIds.has(step.id));
    const deduplicatedSteps = deduplicateStepsById(knownSteps);
    const orderOf = (step: WithRef<IPracticeSetupStep>): number =>
      stepOrderIndex.get(step.id) ?? Infinity;
    return deduplicatedSteps
      .slice()
      .sort((stepA, stepB) => orderOf(stepA) - orderOf(stepB));
  }

  private _groupStepsByCategory(
    steps: WithRef<IPracticeSetupStep>[]
  ): Map<PracticeSetupStepCategory, WithRef<IPracticeSetupStep>[]> {
    const stepsByCategory = new Map<
      PracticeSetupStepCategory,
      WithRef<IPracticeSetupStep>[]
    >();
    steps.forEach((step) => {
      const currentSteps = stepsByCategory.get(step.category) ?? [];
      currentSteps.push(step);
      stepsByCategory.set(step.category, currentSteps);
    });
    return stepsByCategory;
  }

  private async _validateState(
    wizard: WithRef<IPracticeSetupWizard>,
    region: Region
  ): Promise<void> {
    await PracticeSetupWizard.validateAllSteps(wizard.ref, region).catch();
  }
}

function deduplicateStepsById(
  steps: WithRef<IPracticeSetupStep>[]
): WithRef<IPracticeSetupStep>[] {
  const seen = new Set<string>();
  return steps.filter((step) => {
    if (seen.has(step.id)) {
      return false;
    }
    seen.add(step.id);
    return true;
  });
}
